import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

interface ConfirmResult {
  ok: boolean;
  mensaje: string;
  estado?: string;
  id?: number;
  nombreCliente?: string;
  fechaReserva?: string;
  horaReserva?: string;
  sucursalNombre?: string;
}

export default function ConfirmarReservaPage() {
  const { token } = useParams<{ token: string }>();
  const [result, setResult] = useState<ConfirmResult | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    fetch(`/api/v1/reservas/confirmar-email/${token}`)
      .then(r => r.json())
      .then(setResult)
      .catch(() => setResult({ ok: false, mensaje: "Error al procesar la confirmación." }))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center text-gray-400">
          <div className="text-4xl mb-3 animate-pulse">⏳</div>
          <p>Confirmando tu reserva...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-8 text-center">
        {result?.ok ? (
          <>
            <div className="text-6xl mb-4">✅</div>
            <h1 className="font-bold text-2xl text-gray-900 mb-2">¡Reserva confirmada!</h1>
            <p className="text-gray-500 mb-6">{result.mensaje}</p>
            {result.id && (
              <div className="bg-gray-50 rounded-xl p-4 text-left space-y-2 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Reserva</span>
                  <span className="font-semibold text-gray-800">#{result.id}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Cliente</span>
                  <span className="font-semibold text-gray-800">{result.nombreCliente}</span>
                </div>
                {result.fechaReserva && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Fecha</span>
                    <span className="font-semibold text-gray-800">
                      {new Date(result.fechaReserva + "T00:00:00").toLocaleDateString("es-VE", {
                        weekday: "long", year: "numeric", month: "long", day: "numeric"
                      })}
                    </span>
                  </div>
                )}
                {result.horaReserva && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Hora</span>
                    <span className="font-semibold text-gray-800">{result.horaReserva}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Restaurante</span>
                  <span className="font-semibold text-gray-800">{result.sucursalNombre}</span>
                </div>
              </div>
            )}
            <p className="text-sm text-gray-400">¡Te esperamos! Muestra este email al llegar.</p>
          </>
        ) : (
          <>
            <div className="text-6xl mb-4">
              {result?.estado === "confirmada" ? "✅" : result?.estado === "cancelada" ? "❌" : "⚠️"}
            </div>
            <h1 className="font-bold text-xl text-gray-900 mb-2">
              {result?.estado === "confirmada"
                ? "Reserva ya confirmada"
                : result?.estado === "cancelada"
                ? "Reserva cancelada"
                : "No se pudo confirmar"}
            </h1>
            <p className="text-gray-500">{result?.mensaje}</p>
          </>
        )}
      </div>
    </div>
  );
}
